<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-3 sidebar-monitoring bg-custom-blue py-3">

        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">{{ $t("views.nav.manageKPIs") }}</h4>
          <button @click="openModal(false, 'kpi', 'add')" type="button" class="btn btn-sm btn-primary" data-bs-toggle="modal" data-bs-target="#kpiEditModal">+ {{ $t("general.add") }}</button>
        </div>

        <!-- Time-Filter -->
        <div class="row mt-4">
          <div class="col-6">
            <div class="dropdown">
              <button class="btn btn-sm dropdown-toggle bg-white text-start" style="width: 200px" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                <span>{{ formatDate(new Date(filterValidFrom)) }} &nbsp;-&nbsp; {{ formatDate(new Date(filterValidTo)) }}</span>
              </button>
              <div class="dropdown-menu border-secondary p-3">
                <div class="row">
                  <div class="col-12">
                    <button @click="filterKPIs(new Date().toISOString().slice(0, 10), new Date().toISOString().slice(0, 10))" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ $t("general.today") }}</button>
                    <button @click="filterKPIs((new Date().getFullYear() + 1).toString() + '-01-01', (new Date().getFullYear() + 1).toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() + 1 }}</button>
                    <button @click="filterKPIs(new Date().getFullYear().toString() + '-01-01', new Date().getFullYear().toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() }}</button>
                    <button @click="filterKPIs((new Date().getFullYear() - 1).toString() + '-01-01', (new Date().getFullYear() - 1).toString() + '-12-31')" class="btn btn-sm btn-outline-primary me-2 mb-2" type="button">{{ new Date().getFullYear() - 1 }}</button>
                  </div>
                </div>
                <hr class="dropdown-divider">
                <div class="row">
                  <div class="col-6">
                    <label for="filterValidFrom" class="form-label">{{ $t("views.kpi.periods.from") }}:</label>
                    <input v-model="filterValidFrom" type="date" class="form-control form-control-sm" id="filterValidFrom">
                  </div>
                  <div class="col-6">
                    <label for="filterValidTo" class="form-label">{{ $t("views.kpi.periods.to") }}:</label>
                    <input v-model="filterValidTo" type="date" class="form-control form-control-sm" id="filterValidTo">
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <button @click="filterKPIs(filterValidFrom, filterValidTo)" class="btn btn-sm btn-outline-primary mt-3" type="button">{{ $t("views.kpi.periods.filter") }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Search & Slider -->
        <div class="row mt-1">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Search -->
              <div class="me-3" style="width: 200px"> <input v-model="searchText" type="text" :placeholder="$t('general.placeholderSearch')" class="form-control form-control-sm mt-1" />
              </div>
              <!-- Level-Slider from 2 to maxTreeLevel -->
              <div class="d-flex flex-column align-items-start mb-2">
                <div class="fs-08 text-muted mb-1"> {{ $t('general.treeViewLevels') }}: {{ autoOpenDepth }} </div>
                <div class="d-flex align-items-center gap-2">
                  <input type="range" class="form-range foc-range" min="2" :max="maxTreeDepth" step="1" v-model="autoOpenDepth" :key="sliderKey">
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <KPITree v-if="'nodes' in kpis && kpis.nodes.length" :node="kpis" :depth="0" mode="manage" :searchText="searchText" :autoOpenDepth="autoOpenDepth" class="mt-2"></KPITree>
          </div>
        </div>
      </div>
      <div class="col-9 px-5 py-3">
        <div v-if="Object.keys(selectedKPI).length" class="row">
          <div class="col-12">
            <div class="row">
              <div class="col">
                <button v-if="selectedKPI.write" @click="openModal(selectedKPI.id, 'kpi', 'update')" type="button" class="btn btn-sm btn-outline-dark" data-bs-toggle="modal" data-bs-target="#kpiEditModal">&bull; {{ $t("general.update") }}</button>
              </div>
            </div>

            <!-- Name & Heading Data -->
            <div class="row mt-3">
              <div class="col">

                <div class="h4">{{ selectedKPI.name }}</div>

                <!-- Folder description -->
                <div v-if="selectedKPI.folder">
                  <div v-if="selectedKPI.description" class="mt-3">
                    <QuillContent :content="this.selectedKPI.description" />
                  </div>
                </div>

                <ul v-if="!selectedKPI.folder" class="list-group list-group-horizontal-md foc-list-header">
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.responsible") }}:</div>
                    <div>{{ selectedKPI.responsible.name }}</div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.structure.validityPeriod") }}:</div>
                      <div>
                        <span v-if="selectedKPI.validFrom"> {{ formatDate(new Date(selectedKPI.validFrom)) }} {{ $t("general.until") }} {{ formatDate(new Date(selectedKPI.validTo)) }} </span>
                        <span v-else> {{ $t("views.kpi.structure.infinite") }} </span>
                      </div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.unit") }}:</div>
                    <div>{{ selectedKPI.unit }}</div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.decimals") }}:</div>
                    <div>{{ selectedKPI.decimals }}</div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.timeDimInput") }}:</div>
                    <div>
                      <span v-if="selectedKPI.timeDimInput === 'M'"> {{ $t("general.month") }} </span>
                      <span v-if="selectedKPI.timeDimInput === 'Q'"> {{ $t("general.quarter") }} </span>
                      <span v-if="selectedKPI.timeDimInput === 'Y'"> {{ $t("general.year") }} </span>
                    </div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.timeDimDisplay") }}:</div>
                    <div>
                      <span v-if="selectedKPI.timeDimDisplay === 'M'"> {{ $t("general.month") }} </span>
                      <span v-if="selectedKPI.timeDimDisplay === 'Q'"> {{ $t("general.quarter") }} </span>
                      <span v-if="selectedKPI.timeDimDisplay === 'Y'"> {{ $t("general.year") }} </span>
                    </div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.aggregation") }}:</div>
                    <div>
                      <span v-if="selectedKPI.aggregation === 'S'"> {{ $t("views.kpi.edit.sum") }} </span>
                      <span v-if="selectedKPI.aggregation === 'M'"> {{ $t("views.kpi.edit.mean") }} </span>
                      <span v-if="selectedKPI.aggregation === 'A'"> {{ $t("views.kpi.edit.ultimate") }} </span>
                    </div>
                  </li>
                  <li class="list-group-item bg-white border-0">
                    <div class="foc-list-group-item-label">{{ $t("views.kpi.edit.status") }}:</div>
                    <div>
                      <span v-if="selectedKPI.rating === 'H'"> {{ $t("views.kpi.edit.rating.H") }} </span>
                      <span v-if="selectedKPI.rating === 'L'"> {{ $t("views.kpi.edit.rating.L") }} </span>
                      <span v-if="selectedKPI.rating === 'N'"> {{ $t("views.kpi.edit.rating.N") }} </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <!-- KPI Chart -->
            <div v-if="!selectedKPI.folder" class="row mt-3">
              <div class="col-3">
                <div class="card">
                  <div class="card-header text-truncate">
                    {{ $t("views.kpi.edit.unit") }}: {{ selectedKPI.unit }}
                  </div>
                  <div class="card-body text-center py-1">
                    <div class="mt-1">{{ selectedKPI.displayedPeriod ? selectedKPI.displayedPeriod : new Date().getFullYear() }}</div>
                    <KPIPeriodSingleChart :key="'kpi-chart-single-' + kpiKeyInt + selectedKPI.id + curRecord().period" :chartId="'kpiChartSingle' + kpiKeyInt + selectedKPI.id + curRecord().period" :period="curRecord()" class="kpi-chart"></KPIPeriodSingleChart>
                  </div>
                  <div class="card-footer">
                    <div class="row">
                      <div class="col-6">
                        <button class="btn btn-sm btn-outline-primary" :class="!selectedKPI.records ? 'disabled' : ''" data-bs-toggle="modal" :data-bs-target="'#kpiPeriodsModal' + selectedKPI.id">{{ $t("views.kpi.detail.viewKPI") }}</button>
                      </div>
                      <div class="col-4 offset-2 text-end my-auto">
                        <div class="progress position-relative border bg-dark border-dark">
                          <span class="progress-label position-absolute start-50 top-50 text-light fw-bold">{{ getProgressStatus }}%</span>
                          <div class="progress-bar progress-bar-striped" :class="getProgressStatus >= 100 ? 'bg-success' : 'bg-danger'" :style="'width: ' + getProgressStatus + '%'" role="progressbar" :aria-valuenow="getProgressStatus" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- KPI Registerkarten -->
            <div v-if="!selectedKPI.folder" class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header fw-bold">{{ $t("views.kpi.detail.headingCard") }}</div>
                  <div class="card-body">
                    <ul class="nav nav-tabs border-bottom" role="tablist">
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark active" id="definition-tab" data-bs-toggle="tab" data-bs-target="#definition" type="button" role="tab" aria-controls="definition" aria-selected="false">{{ $t("views.kpi.detail.tabDefinition") }}</button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark" id="formulas-tab" data-bs-toggle="tab" data-bs-target="#formulas" type="button" role="tab" aria-controls="formulas" aria-selected="false">{{ $t("views.kpi.detail.tabFormulas") }} <span v-if="selectedKPI.formulas.length" class="badge bg-primary">{{ selectedKPI.formulas.length }}</span></button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button class="nav-link text-dark" id="relationships-tab" data-bs-toggle="tab" data-bs-target="#relationships" type="button" role="tab" aria-controls="relationships" aria-selected="false">{{ $t("views.relationships.tabRelationships") }} <span v-if="selectedKPI.relationships.length" class="badge bg-primary">{{ selectedKPI.relationships.length }}</span></button>
                      </li>
                    </ul>
                    <div class="tab-content mt-2">
                      <div class="tab-pane fade show active" id="definition" role="tabpanel">
                        <div v-if="selectedKPI.description"  class="mt-3">
                          <QuillContent :content="selectedKPI.description"></QuillContent>
                        </div>
                        <div v-else class="mt-3">{{ $t("views.kpi.detail.noDescription") }}</div>
                      </div>
                      <div class="tab-pane fade" id="formulas" role="tabpanel">
                        <div v-if="selectedKPI.write" class="row my-3">
                          <div class="col" v-if="selectedKPI.formulas.length === 0">
                            <button @click="openModal(false, 'formula', 'add')" type="button" class="btn btn-sm btn-outline-primary" data-bs-toggle="modal" data-bs-target="#kpiEditModal">+ {{ $t("general.add") }}</button>
                          </div>
                        </div>
                        <div v-if="selectedKPI.formulas.length">
                          <table class="table">
                            <thead>
                              <tr class="border-dark">
                                <th scope="col">{{ $t("views.kpi.edit.formula") }}</th>
                                <th scope="col">{{ $t("views.kpi.edit.validFrom") }}</th>
                                <th scope="col">{{ $t("views.kpi.edit.validTo") }}</th>
                                <th scope="col"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="formula in selectedKPI.formulas" :key="'formula' + formula.id">
                                <th scope="row" class="fw-normal">
                                  <span v-for="element in formula.formula" :key="'formula' + formula.id + 'Element' + element.id" class="me-1">{{ element.name }}</span>
                                </th>
                                <td>{{ formula.validFrom }}</td>
                                <td>{{ formula.validTo }}</td>
                                <td v-if="selectedKPI.write">
                                  <div class="dropdown">
                                    <a href="#" class="ms-2 text-dark" data-bs-toggle="dropdown">
                                      <svg class="bi" width="28" height="28" fill="currentColor">
                                        <use xlink:href="@/assets/icons/icons.svg#three-dots"/>
                                      </svg>
                                    </a>
                                    <ul class="dropdown-menu">
                                      <li><a @click="openModal(formula.id, 'formula', 'update')" href="#kpiEditModal" class="dropdown-item" data-bs-toggle="modal">{{ $t("general.update") }}</a></li>
                                      <li><a @click="openModal(formula.id, 'formula', 'delete')" href="#kpiDeleteModal" class="dropdown-item" data-bs-toggle="modal">{{ $t("general.delete") }}</a></li>
                                    </ul>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="mt-3 h6">{{ $t("views.kpi.detail.noFormulas") }}</div>
                      </div>
                      <div class="tab-pane fade" id="relationships" role="tabpanel">
                        <div v-if="selectedKPI.relationships.length">
                          <table class="table">
                            <thead>
                              <tr class="border-dark">
                                <th scope="col">{{ $t("views.relationships.headerLabel") }}</th>
                                <th scope="col">{{ $t("views.relationships.headerObject") }}</th>
                                <th scope="col">{{ $t("views.relationships.headerValidFrom") }}</th>
                                <th scope="col">{{ $t("views.relationships.headerValidTo") }}</th>
                                <th scope="col"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="relationship in selectedKPI.relationships" :key="'relationship' + relationship.id">
                                <td v-if="relationship.type === 'objective'">
                                  <div class="cont-icon-org-unit d-inline-block me-1">
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconObjective'"> <use xlink:href="@/assets/icons/icons.svg#objective" /> </svg> &nbsp;
                                    {{ $t("views.relationships.kpiObjective") }}
                                  </div>
                                </td>
                                <td v-if="relationship.type === 'strategy'">
                                  <div class="cont-icon-org-unit d-inline-block me-1">
                                    <svg class="bi" width="16" height="16" fill="currentColor" v-tooltip="'tooltip.iconStrategy'"> <use xlink:href="@/assets/icons/icons.svg#strategy" /> </svg> &nbsp;
                                    {{ $t("views.relationships.kpiStrategy") }}
                                  </div>
                                </td>
                                <td v-if="relationship.type === 'objective'"> <a :href="createLink('objective', relationship.objectiveUuid)" target="_blank"> {{ relationship.category === 1 ? relationship.name.toUpperCase() : relationship.name }} </a> </td>
                                <td v-if="relationship.type === 'strategy'"> <a :href="createLink('strategy', relationship.strategyUuid)" target="_blank"> {{ relationship.name }} </a> </td>
                                <td>{{ formatDate(new Date(relationship.validFrom)) }}</td>
                                <td>{{ formatDate(new Date(relationship.validTo)) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="mt-3 h6">{{ $t("views.relationships.noRelationships") }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <KPIPeriodsModal :key="'kpi-chart-multi-' + kpiKeyInt + selectedKPI.id" :kpi="selectedKPI"></KPIPeriodsModal>
            <KPIDeleteModal></KPIDeleteModal>
          </div>
        </div>
        <div v-else class="text-center mt-5">
          <img src="@/assets/img/no_data.svg" class="img-fluid" width="250">
          <p v-if="'nodes' in kpis && kpis.nodes.length" class="h5 mt-3">{{ $t("views.kpi.structure.helpChooseKPI") }}</p>
          <p v-else class="h5 mt-3">{{ $t("views.kpi.structure.helpCreateKPI") }}</p>
        </div>
      </div>
    </div>
    <KPIEditModal></KPIEditModal>
  </div>
</template>

<script>
import KPITree from '../../components/monitoring/KPITree.vue'
import KPIEditModal from '../../components/monitoring/KPIEditModal.vue'
import KPIDeleteModal from '../../components/monitoring/KPIDeleteModal.vue'
import KPIPeriodSingleChart from '../../components/charts/KPIPeriodSingle.vue'
import KPIPeriodsModal from '../../components/monitoring/KPIPeriodsModal.vue'
import QuillContent from '@/components/QuillContent.vue'
import * as formatUtils from '../../composables/formatUtils'
import * as tree from '@/composables/tree'

export default {
  name: 'MonitoringManageKPI',
  data () {
    return {
      kpiKeyInt: 0,
      kpiModalElementId: false,
      kpiModalElementType: '',
      kpiModalMode: '',
      selectedElement: '',
      filterValidFrom: this.$store.state.user?.settings?.favorites?.kpiManage?.filterValidFrom ? this.$store.state.user.settings.favorites.kpiManage.filterValidFrom : new Date().getFullYear().toString() + '-01-01',
      filterValidTo: this.$store.state.user?.settings?.favorites?.kpiManage?.filterValidTo ? this.$store.state.user.settings.favorites.kpiManage.filterValidTo : (new Date().getFullYear() + 1).toString() + '-12-31',
      searchText: '',
      autoOpenDepth: 2,
      sliderKey: 0
    }
  },
  components: {
    KPITree,
    KPIEditModal,
    KPIDeleteModal,
    KPIPeriodSingleChart,
    KPIPeriodsModal,
    QuillContent
  },
  computed: {
    sysParms () {
      return this.$store.state.sysParms
    },
    kpis () {
      return this.$store.state.kpis.constructor === Array ? [] : this.$store.state.kpis
    },
    selectedKPI () {
      return this.$store.state.selectedKPI
    },

    // maximale Strukturtiefe ermitteln (Fallback 2)
    maxTreeDepth () {
      return tree.getMaxTreeDepth(this.$store.state.kpis?.nodes, 2)
    },

    getProgressStatus () {
      let progressStatus = 0
      if (this.curRecord().forecast && this.curRecord().objective) {
        progressStatus = Math.round((this.unFormatNumber(this.curRecord().forecast) / this.unFormatNumber(this.curRecord().objective)) * 100)
      }
      return progressStatus
    },
    statusRedRelationalOperator () {
      return this.selectedKPI.statusRed <= 100 ? '<' : '>'
    },
    statusYellowRelationalOperator () {
      return this.selectedKPI.statusRed < 100 ? '<' : '>'
    }
  },

  watch: {
    'kpis.nodes': {
      handler (nodes) {
        if (!nodes || nodes.length === 0) return

        // Set Slider Default-Level (Fallback = 2)
        const depth = this.maxTreeDepth
        const defaultLevel = this.sysParms?.kpiTreeDefaultLevel ?? 2
        this.autoOpenDepth = Math.min(Math.max(defaultLevel, 2), depth)

        // Render Slider
        this.sliderKey++
      },
      immediate: true
    }
  },

  methods: {
    async filterKPIs (filterFrom, filterTo) {
      this.$store.commit('SET_LOADING_STATUS', true)
      this.filterValidFrom = filterFrom
      this.filterValidTo = filterTo
      await this.$store.dispatch('getKPIs', { validFrom: filterFrom, validTo: filterTo })
      await this.$store.dispatch('favoriteObject', { isList: false, objectKey: 'kpiManage', objectValue: { filterValidFrom: filterFrom, filterValidTo: filterTo } })
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    createLink (type, uuid) {
      let url = ''
      if (type === 'objective') url = '/objectives/manage/objectives?id=' + uuid
      if (type === 'strategy') url = '/strategies/manage/strategies?id=' + uuid
      return url
    },
    openModal (id, elementType, mode) {
      if (id) {
        this.kpiModalElementId = id
      }
      this.kpiModalElementType = elementType
      this.kpiModalMode = mode
    },
    async selectObjective (objectiveId) {
      this.$store.commit('SET_LOADING_STATUS', true)
      await this.$store.dispatch('getObjective', objectiveId).then(() => {
        this.$router.push({ name: 'ObjectivesManageObjective' })
      })
      this.$store.commit('SET_LOADING_STATUS', false)
    },
    curRecord () {
      const curRecord = this.selectedKPI.records?.find(record => {
        return record.period === new Date().getFullYear().toString()
      })
      if (curRecord) {
        const prevYearRecord = this.selectedKPI.records?.find(record => {
          return parseInt(record.period) === parseInt(curRecord.period) - 1
        })
        if (prevYearRecord) {
          curRecord.actualPrev = prevYearRecord.actual
        }
        this.selectedKPI.displayedPeriod = curRecord.period
        return curRecord
      } else {
        return {}
      }
    },
    formatDate (date) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.formatDate(userLocale, date)
    },
    unFormatNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.unFormatNumber(userLocale, number)
    }
  },

  async created () {
    this.$store.commit('SET_LOADING_STATUS', true)

    // check query params for selected KPI
    let paramSelectedKPI = ''
    if (this.$route.query.id) {
      paramSelectedKPI = this.$route.query.id
    }

    // get filter values from param object
    if (paramSelectedKPI !== '') {
      await this.$store.dispatch('getKPI', { mode: 'manage', id: paramSelectedKPI })
      this.searchText = this.selectedKPI.name
      this.filterValidFrom = this.selectedKPI.validFrom || formatUtils.getTodayIso()
      this.filterValidTo = this.selectedKPI.validTo || formatUtils.getTodayIso()
    }

    await this.$store.dispatch('getKPIs', { validFrom: this.filterValidFrom, validTo: this.filterValidTo })
    this.$store.commit('SET_LOADING_STATUS', false)

    if (Object.keys(this.selectedKPI).length) {
      await this.$store.dispatch('getKPI', { mode: 'manage', id: this.selectedKPI.id })
    }
    this.kpiKeyInt++
    await this.$store.dispatch('getKPIUnits')
    await this.$store.dispatch('getEmployees', { mode: 'default' })
  }
}
</script>

<style scoped>
.kpi-chart {
  max-height: 200px;
}
.progress-label {
  transform: translate(-50%, -50%);
}
</style>
